import { marked } from "marked";
import DOMPurify from "dompurify";
import type { Guide, GuideFrontmatter } from "./types";

/**
 * Result of parsing frontmatter from a markdown file.
 */
export interface ParsedFrontmatter {
  frontmatter: GuideFrontmatter;
  content: string;
}

/**
 * Parses YAML frontmatter from markdown content.
 * Frontmatter must be enclosed in --- delimiters at the start of the file.
 *
 * @param markdown - Raw markdown content with frontmatter
 * @returns Parsed frontmatter and remaining content
 * @throws Error if frontmatter is missing or invalid
 */
export function parseFrontmatter(markdown: string): ParsedFrontmatter {
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/;
  const match = markdown.match(frontmatterRegex);

  if (!match) {
    throw new Error("Guide must have frontmatter delimited by ---");
  }

  const [, yamlContent, bodyContent] = match;
  const frontmatter = parseYamlFrontmatter(yamlContent);

  if (!frontmatter.title) {
    throw new Error("Guide must have a title in frontmatter");
  }

  return {
    frontmatter,
    content: bodyContent.trim(),
  };
}

/**
 * Simple YAML parser for frontmatter.
 * Supports: title, order (number), keywords (array).
 */
function parseYamlFrontmatter(yaml: string): GuideFrontmatter {
  const lines = yaml.split("\n");
  const result: GuideFrontmatter = { title: "" };

  let currentKey = "";
  let inArray = false;
  const arrayValues: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Handle inline array format: keywords: [one, two, three]
    const inlineArrayMatch = line.match(/^(\w+):\s*\[(.*)\]\s*$/);
    if (inlineArrayMatch) {
      const [, key, values] = inlineArrayMatch;
      if (key === "keywords") {
        result.keywords = values.split(",").map((v) => v.trim());
      }
      continue;
    }

    // Handle key: value pairs
    const keyValueMatch = line.match(/^(\w+):\s*(.*)$/);
    if (keyValueMatch) {
      // Save previous array if we were collecting one
      if (inArray && currentKey === "keywords") {
        result.keywords = [...arrayValues];
        arrayValues.length = 0;
      }

      const [, key, value] = keyValueMatch;
      currentKey = key;

      if (value === "" || value === undefined) {
        // Might be starting an array
        inArray = true;
        continue;
      }

      inArray = false;

      if (key === "title") {
        result.title = value;
      } else if (key === "order") {
        result.order = parseInt(value, 10);
      } else if (key === "keywords") {
        // Single keyword on same line as key
        result.keywords = [value];
      }
      continue;
    }

    // Handle array items (- value)
    if (inArray && trimmed.startsWith("-")) {
      const value = trimmed.slice(1).trim();
      arrayValues.push(value);
    }
  }

  // Save final array if we were collecting one
  if (inArray && currentKey === "keywords" && arrayValues.length > 0) {
    result.keywords = [...arrayValues];
  }

  return result;
}

/**
 * Renders markdown to sanitised HTML.
 * Uses marked for parsing and DOMPurify for XSS protection.
 *
 * @param markdown - Raw markdown content
 * @returns Sanitised HTML string
 */
export function renderMarkdown(markdown: string): string {
  // Configure marked for synchronous parsing
  const rawHtml = marked.parse(markdown, { async: false }) as string;

  // Sanitise HTML to prevent XSS attacks
  const cleanHtml = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      "h1", "h2", "h3", "h4", "h5", "h6",
      "p", "br", "hr",
      "ul", "ol", "li",
      "pre", "code",
      "blockquote",
      "strong", "em", "b", "i", "u", "s",
      "a", "img",
      "table", "thead", "tbody", "tr", "th", "td",
    ],
    ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "style"],
    FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover"],
    ALLOW_DATA_ATTR: false,
  });

  return addContentStyles(cleanHtml);
}

/**
 * Adds inline styles to HTML elements for consistent spacing and list styling.
 * This ensures styles are applied regardless of Tailwind JIT compilation.
 */
function addContentStyles(html: string): string {
  return html
    .replace(/<h1>/g, '<h1 style="margin-bottom: 2rem;">')
    .replace(/<h2>/g, '<h2 style="margin-bottom: 1.5rem;">')
    .replace(/<h3>/g, '<h3 style="margin-bottom: 1rem;">')
    .replace(/<h4>/g, '<h4 style="margin-bottom: 1rem;">')
    .replace(/<p>/g, '<p style="margin-bottom: 1.5rem;">')
    .replace(/<ul>/g, '<ul style="margin-bottom: 1.5rem; list-style-type: disc; padding-left: 1.5rem;">')
    .replace(/<ol>/g, '<ol style="margin-bottom: 1.5rem; list-style-type: decimal; padding-left: 1.5rem;">')
    .replace(/<li>/g, '<li style="margin-bottom: 0.5rem;">');
}

/**
 * Parses a complete guide from markdown content.
 *
 * @param slug - Unique identifier for the guide (derived from filename)
 * @param markdown - Raw markdown content with frontmatter
 * @returns Complete Guide object ready for indexing and display
 */
export function parseGuide(slug: string, markdown: string): Guide {
  const { frontmatter, content } = parseFrontmatter(markdown);
  const html = renderMarkdown(content);

  return {
    slug,
    title: frontmatter.title,
    keywords: frontmatter.keywords ?? [],
    order: frontmatter.order ?? 999,
    content,
    html,
  };
}
